import { createFileRoute } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { DrawPad } from "@/components/DrawPad";
import { DRAW_INDEX_SIZE } from "@/lib/draw-recognize";

export const Route = createFileRoute("/draw")({
  head: () => ({
    meta: [
      { title: "Draw to find a symbol — sketch any character to identify it" },
      {
        name: "description",
        content:
          "Don't know a symbol's name? Draw it. Sketch a shape and this on-device recogniser ranks the closest Unicode symbols — math signs, arrows, shapes, currency and more — each linking to its detail page. No upload, works offline.",
      },
      { property: "og:title", content: "Draw to find a symbol" },
      {
        property: "og:description",
        content: "Sketch a shape and get the closest matching Unicode symbols, ranked on-device.",
      },
      { property: "og:url", content: abs("/draw") },
    ],
    links: [{ rel: "canonical", href: abs("/draw") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Draw to find a symbol",
          applicationCategory: "UtilityApplication",
          operatingSystem: "Any",
          url: "/draw",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }),
      },
    ],
  }),
  component: DrawPage,
});

function DrawPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-12 sm:px-6 sm:py-16">
        <h1 className="font-display text-4xl font-bold sm:text-5xl">
          Draw to <span className="text-gradient">find a symbol</span>
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-foreground/85">
          Seen a symbol but don't know what it's called? Sketch its shape below and the closest{" "}
          {DRAW_INDEX_SIZE} line-drawable Unicode symbols are ranked instantly — tap any match to
          open its page or copy it.
        </p>

        <DrawPad />

        <section aria-labelledby="how-heading" className="mt-14 max-w-2xl">
          <h2 id="how-heading" className="font-display text-xl font-semibold">
            How the recognition works
          </h2>
          <p className="mt-3 text-foreground/85">
            This is a self-hosted shape matcher — nothing you draw leaves your device and no
            external service is called. At build time every line-drawable glyph is traced from its
            real outline and reduced to a small occupancy grid describing where its strokes fall.
            Your sketch is reduced to the same grid and compared to all {DRAW_INDEX_SIZE} of them by
            nearest-neighbour similarity, so the ranking is instant and works fully offline. Colour
            emoji are excluded — they aren't line shapes — so the index focuses on math signs,
            arrows, geometric shapes, punctuation, currency and letterforms. The bigger and cleaner
            your strokes, the better the match.
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
